import type { ZombieDef } from "../schema/zombie.js";
import type { AbilityRef } from "../schema/unit.js";
import type { Role, Tier } from "../schema/common.js";
import { ARCHETYPE_NAMES } from "./archetypes.js";
import { PROTOCOLS } from "../schema/ability.js";

function a(archetype: string, params: Record<string, number> = {}, name?: [string, string]): AbilityRef {
  const n = name ?? ARCHETYPE_NAMES[archetype] ?? [archetype, archetype];
  return { archetype, name: { de: n[0], en: n[1] }, params };
}

function zmb(
  id: string,
  name: [string, string],
  roleAnalogue: Role,
  tier: Tier,
  statMult: ZombieDef["statMult"],
  abilities: AbilityRef[],
  description: [string, string],
  boss = false,
): ZombieDef {
  return {
    id,
    name: { de: name[0], en: name[1] },
    roleAnalogue,
    tier,
    boss,
    statMult,
    abilities,
    variants: ["fresh", "decayed", "mutated"],
    description: { de: description[0], en: description[1] },
  };
}

/** Zombie archetypes (40-combat.md R34). `statMult` scales the role base stats; the first ability is the standard attack. */
export const ZOMBIES: readonly ZombieDef[] = [
  zmb("zmb.shambler", ["Schlurfer", "Shambler"], "attacker", "D", { hp: 0.9, atk: 0.8, def: 0.7, speed: 1.3 },
    [a("abl.single_strike", { mult: 0.9 }, ["Biss", "Bite"])],
    ["Der gewöhnliche Infizierte. Langsam, zahlreich, hungrig.", "The common infected. Slow, numerous, hungry."]),
  zmb("zmb.runner", ["Läufer", "Runner"], "saboteur", "C", { hp: 0.7, atk: 1.0, def: 0.5, speed: 0.8 },
    [a("abl.single_strike", { mult: 1.0 }, ["Sprung", "Lunge"]), a("abl.snipe_rear", { mult: 1.3 }, ["Überspringen", "Vault"])],
    ["Rennt aufrecht, springt über Linien, überrascht die Nachhut.", "Runs upright, leaps over lines, surprises the rear."]),
  zmb("zmb.bloater", ["Blähling", "Bloater"], "attacker", "C", { hp: 1.3, atk: 0.7, def: 0.6, speed: 1.4 },
    [a("abl.single_strike", { mult: 0.7 }, ["Schlag", "Slam"]), a("abl.explode_on_death", { mult: 1.8 })],
    ["Ein wandelnder Gasballon. Stirbt laut.", "A walking gas balloon. Dies loudly."]),
  zmb("zmb.brute", ["Brecher", "Brute"], "defender", "B", { hp: 1.6, atk: 0.9, def: 1.2, speed: 1.2 },
    [a("abl.single_strike", { mult: 1.0 }, ["Prankenhieb", "Claw"]), a("abl.taunt", { ticks: 30 }), a("abl.fortify", { pct: 20, ticks: 40 })],
    ["Muskeln, die weiterwachsen, nachdem der Mensch aufgehört hat.", "Muscles that kept growing after the human stopped."]),
  zmb("zmb.spitter", ["Spucker", "Spitter"], "attacker", "C", { hp: 0.8, atk: 1.1, def: 0.6, speed: 1.1 },
    [a("abl.single_strike", { mult: 0.9 }, ["Spucke", "Spit"]), a("abl.corrode", { pctPerSec: 4, ticks: 50 })],
    ["Spuckt Säure über die halbe Straße.", "Spits acid across half the street."]),
  zmb("zmb.screamer", ["Schreier", "Screamer"], "support", "B", { hp: 0.9, atk: 0.5, def: 0.7, speed: 1.3 },
    [a("abl.single_strike", { mult: 0.6 }, ["Kratzer", "Scratch"]), a("abl.summon", { count: 2, every: 60 })],
    ["Ihr Schrei ruft, was in den Ruinen schläft.", "Her scream calls what sleeps in the ruins."]),
  zmb("zmb.crawler", ["Kriecherschwarm", "Crawler swarm"], "attacker", "D", { hp: 0.6, atk: 0.9, def: 0.4, speed: 0.9 },
    [a("abl.multi_strike", { targets: 3, mult: 0.5 }, ["Schwarmbiss", "Swarm Bite"])],
    ["Dutzende auf allen vieren, gezählt als eine.", "Dozens on all fours, counted as one."]),
  zmb("zmb.stalker", ["Pirscher", "Stalker"], "saboteur", "A", { hp: 0.8, atk: 1.4, def: 0.6, speed: 0.85 },
    [a("abl.single_strike", { mult: 1.1 }, ["Krallenstoß", "Talon Strike"]), a("abl.execute", { threshold: 30, bonus: 100 }), a("abl.blind", { pct: 20, ticks: 30 })],
    ["Wartet, bis einer schwächelt. Dann ist er dran.", "Waits for one to weaken. Then it is his turn."]),
  zmb("zmb.hulk", ["Koloss", "Hulk"], "defender", "A", { hp: 2.2, atk: 1.2, def: 1.4, speed: 1.4 },
    [a("abl.single_strike", { mult: 1.2 }, ["Rammstoß", "Ram"]), a("abl.armor_break", { pct: 30, ticks: 40 }), a("abl.bulwark", { cap: 25 })],
    ["Ein Elite-Infizierter, der Panzerplatten wie Blech biegt.", "An elite infected that bends armour plate like tin."]),
  zmb("zmb.necromancer", ["Nekromant", "Necromancer"], "commander", "A", { hp: 1.0, atk: 0.9, def: 0.9, speed: 1.0 },
    [a("abl.command_pulse", { mult: 0.8, rule: PROTOCOLS.indexOf("lowest_hp") }, ["Totenruf", "Death Call"]), a("abl.pulse_rally", { pct: 15, ticks: 30 }, ["Hordenrausch", "Horde Frenzy"]), a("abl.ultimate_reboot", { pct: 40 }, ["Wiedererweckung", "Reanimate"])],
    ["Das Strain-Bewusstsein spricht durch ihn. Die Horde gehorcht.", "The strain's consciousness speaks through him. The horde obeys."]),
  zmb("zmb.carrier", ["Träger", "Carrier"], "support", "B", { hp: 1.1, atk: 0.5, def: 0.8, speed: 1.2 },
    [a("abl.single_strike", { mult: 0.6 }, ["Sporenwolke", "Spore Cloud"]), a("abl.aura", { stat: 2, pct: 8 }, ["Sporenaura", "Spore Aura"]), a("abl.cleanse", { count: 2 })],
    ["Sporen aus jeder Pore. Die Horde heilt in seiner Nähe.", "Spores from every pore. The horde heals near him."]),
  zmb("zmb.abomination", ["Abscheulichkeit", "Abomination"], "defender", "S", { hp: 3.0, atk: 1.5, def: 1.3, speed: 1.3 },
    [a("abl.single_strike", { mult: 1.3 }, ["Doppelschlag", "Double Slam"]), a("abl.aoe_blast", { mult: 0.8 }), a("abl.shock", { ticks: 10, mult: 1.0 }), a("abl.reflect", { pct: 10 })],
    ["Zwei Oberkörper, vier Beine, ein Hunger.", "Two torsos, four legs, one hunger."]),
  // bosses
  zmb("zmb.boss_hive_matriarch", ["Hive-Matriarchin", "Hive Matriarch"], "commander", "S", { hp: 6.0, atk: 1.6, def: 1.5, speed: 1.2 },
    [a("abl.command_pulse", { mult: 1.0, rule: PROTOCOLS.indexOf("highest_atk") }, ["Brutbefehl", "Brood Command"]), a("abl.summon", { count: 3, every: 50 }), a("abl.corrode", { pctPerSec: 5, ticks: 60 }), a("abl.pulse_fortify", { pct: 20, ticks: 40 }), a("abl.ultimate_lockdown", { ticks: 15 })],
    ["Die Königin eines Nests. Ihr Leben bleibt, bis das Nest fällt.", "The queen of a nest. Her HP persists until the hive falls."], true),
  zmb("zmb.boss_titan_necrolith", ["Titan Necrolith", "Titan Necrolith"], "defender", "S", { hp: 40.0, atk: 2.5, def: 2.0, speed: 1.6 },
    [a("abl.single_strike", { mult: 1.5 }, ["Erdbeben", "Quake"]), a("abl.aoe_blast", { mult: 1.0 }), a("abl.armor_break", { pct: 40, ticks: 50 }), a("abl.bulwark", { cap: 10 }), a("abl.explode_on_death", { mult: 2.5 })],
    ["Der Weltboss. Ein wandelnder Berg aus Infizierten, alle 48 Stunden.", "The world boss. A walking mountain of infected, every 48 hours."], true),
];

const zombieMap = new Map(ZOMBIES.map((z) => [z.id, z]));

export function zombieById(id: string): ZombieDef {
  const z = zombieMap.get(id);
  if (!z) throw new Error(`unknown zombie ${id}`);
  return z;
}
